import type { TermsCountry } from './public-payment-terms'

const ALLOWED_TAGS = new Set([
  'section',
  'div',
  'p',
  'span',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'blockquote',
  'pre',
  'code',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'ul',
  'ol',
  'li',
  'table',
  'caption',
  'colgroup',
  'col',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'a',
])
const DROP_CONTENT_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'template',
  'noscript',
  'svg',
  'math',
  'form',
  'input',
  'button',
  'select',
  'textarea',
])
const EMPTY_KR_BLOCKS = new Set(['section', 'div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

function safeHref(value: string): string | null {
  const hasUnsafeCharacter = [...value].some(
    (character) => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127 || character === '\\',
  )
  if (!value || hasUnsafeCharacter) {
    return null
  }
  try {
    const url = new URL(value, window.location.origin)
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : null
  } catch {
    return null
  }
}

/** Rebuild allowlisted inert HTML. Raw nodes, styles, classes, IDs and event handlers are never inserted. */
export function sanitizePaymentTerms(content: string, country: TermsCountry): string {
  if (typeof document === 'undefined') {
    return ''
  }
  const input = document.createElement('template')
  input.innerHTML = content
  const output = document.createElement('template')

  function copyNode(node: Node, parent: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent ?? ''))
      return
    }
    if (!(node instanceof HTMLElement) || node.namespaceURI !== 'http://www.w3.org/1999/xhtml') {
      return
    }
    const tag = node.localName
    if (DROP_CONTENT_TAGS.has(tag)) {
      return
    }
    if (country === 'KR' && tag === 'h1' && node.textContent?.replace(/\s/g, '') === '이용약관') {
      return
    }
    if (!ALLOWED_TAGS.has(tag)) {
      for (const child of node.childNodes) {
        copyNode(child, parent)
      }
      return
    }
    const clean = document.createElement(tag)
    if (tag === 'a') {
      const href = safeHref(node.getAttribute('href') ?? '')
      if (href) {
        clean.setAttribute('href', href)
        clean.setAttribute('target', '_blank')
        clean.setAttribute('rel', 'noopener noreferrer')
      }
    }
    for (const attribute of tag === 'td' || tag === 'th' ? ['colspan', 'rowspan'] : tag === 'ol' ? ['start'] : []) {
      const value = node.getAttribute(attribute)
      if (value && /^\d{1,3}$/.test(value)) {
        clean.setAttribute(attribute, value)
      }
    }
    if (tag === 'th' && ['row', 'col', 'rowgroup', 'colgroup'].includes(node.getAttribute('scope') ?? '')) {
      clean.setAttribute('scope', node.getAttribute('scope')!)
    }
    for (const child of node.childNodes) {
      copyNode(child, clean)
    }
    if (
      country === 'KR' &&
      EMPTY_KR_BLOCKS.has(tag) &&
      !clean.textContent?.trim() &&
      !clean.querySelector('table,hr')
    ) {
      return
    }
    if (
      (country === 'KR' &&
        (tag === 'b' || tag === 'strong') &&
        /^제\s*\d+\s*조/.test(clean.textContent?.trim() ?? '')) ||
      (country === 'JP' && tag === 'h1')
    ) {
      clean.className = 'terms-article-title'
    }
    if (tag === 'table') {
      const wrapper = document.createElement('div')
      wrapper.className = 'terms-table-scroll'
      wrapper.tabIndex = 0
      wrapper.setAttribute('role', 'region')
      wrapper.setAttribute(
        'aria-label',
        country === 'JP' ? '規約の表（横にスクロールできます）' : '약관 표 (가로로 스크롤할 수 있습니다)',
      )
      wrapper.appendChild(clean)
      parent.appendChild(wrapper)
    } else {
      parent.appendChild(clean)
    }
  }
  for (const node of input.content.childNodes) {
    copyNode(node, output.content)
  }
  return output.innerHTML
}
